import { Card, CircularProgress, Stack, Typography } from "@mui/material";
import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { z } from "zod";
import CardWithIcon from "../../components/cards/CardWithIcon";
import TextInput from "../../components/inputs/TextInput";
import { INFO_EMAIL } from "../../config";
import { useAuth } from "../../contexts/AuthContext";
import { useError } from "../../contexts/ErrorContext";
import Translations from "../../lang/en/pages.json";
import Minimal from "../../layouts/Minimal";

const phoneRegex = /^(\+\d{1,3})?\s?\(?\d{1,4}\)?[-.\s]?\d{1,4}[-.\s]?\d{1,9}$/;

const schema = z.object({
  companyName: z
    .string()
    .min(2, Translations.pages.profile_page.errors.company_name.required),
  userName: z
    .string()
    .min(2, Translations.pages.profile_page.errors.user_name.required),
  email: z
    .string({ message: Translations.pages.profile_page.errors.email.required })
    .email(Translations.pages.profile_page.errors.email.invalid),
  phone: z
    .string()
    .min(1, Translations.pages.profile_page.errors.phone.required)
    .regex(phoneRegex, Translations.pages.profile_page.errors.phone.invalid),
});

const Profile: React.FC = () => {
  const [email, setEmail] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [userName, setUserName] = useState("");
  const [phone, setPhone] = useState("");

  const { showErrors, showError } = useError();
  const [isLoading, setIsLoading] = useState(false);

  const navigate = useNavigate();
  const { user } = useAuth();

  const handleSubmit = async () => {};

  useEffect(() => {
    if (user) {
      setEmail(user.email ?? "");
      setPhone(user.phoneNumber ?? "");
      setCompanyName(user.displayName ?? "");
      setUserName(user.displayName ?? "");
    }
  }, [user]);

  const handleCancel = () => {
    navigate("/");
  };

  return (
    <Minimal>
      <Stack width={"20%"} spacing={6} textAlign={"center"}>
        <Stack spacing={0}>
          <Typography
            sx={{ color: "#266A4A" }}
            fontWeight={"bold"}
            variant="h5"
          >
            {Translations.pages.profile_page.title}
          </Typography>
          <Typography
            color={"textPrimary"}
            fontWeight={"light"}
            variant="caption"
          >
            {Translations.pages.profile_page.description.split("{email}")[0]}
            <Link to={`mailto:${INFO_EMAIL}`}>{INFO_EMAIL}</Link>
            {Translations.pages.profile_page.description.split("{email}")[1]}
          </Typography>
        </Stack>
        <Card>
          <Stack spacing={8} textAlign={"center"}>
            <Stack spacing={4}>
              <TextInput
                disabled
                label={
                  Translations.pages.profile_page.fields.company_name.label
                }
                placeholder={
                  Translations.pages.profile_page.fields.company_name
                    .placeholder
                }
                value={companyName}
                onChange={(value: string | number) =>
                  setCompanyName(value as string)
                }
              />
              <TextInput
                disabled
                label={Translations.pages.profile_page.fields.user_name.label}
                placeholder={
                  Translations.pages.profile_page.fields.user_name.placeholder
                }
                value={userName}
                onChange={(value: string | number) =>
                  setUserName(value as string)
                }
              />
              <TextInput
                disabled
                type="email"
                label={Translations.pages.profile_page.fields.email.label}
                placeholder={
                  Translations.pages.profile_page.fields.email.placeholder
                }
                value={email}
                onChange={(value: string | number) => setEmail(value as string)}
              />
              <TextInput
                disabled
                label={Translations.pages.profile_page.fields.phone.label}
                placeholder={
                  Translations.pages.profile_page.fields.phone.placeholder
                }
                value={phone}
                onChange={(value: string | number) => setPhone(value as string)}
              />
            </Stack>
          </Stack>
        </Card>
        <Link to="/">
          <Typography>Back</Typography>
        </Link>
      </Stack>
    </Minimal>
  );
};

export default Profile;
